import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { Dialog, DialogContent, DialogTitle, DialogActions, Button, TextField, Select, MenuItem, FormControl, InputLabel, Chip, Box, Typography, Tabs, Tab, Paper, Grid, Tooltip, IconButton, Switch, FormControlLabel, Slider, Collapse, Divider } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { MdSmartToy } from "react-icons/md";
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ImageIcon from "@material-ui/icons/Image";
import LinkIcon from "@material-ui/icons/Link";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import LibraryBooksIcon from "@material-ui/icons/LibraryBooks";
import CompareArrowsIcon from "@material-ui/icons/CompareArrows";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import ExtensionIcon from "@material-ui/icons/Extension";
import FavoriteBorderIcon from "@material-ui/icons/FavoriteBorder";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import InsertEmoticonIcon from "@material-ui/icons/InsertEmoticon";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventNoteIcon from "@material-ui/icons/EventNote";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import EventBusyIcon from "@material-ui/icons/EventBusy";
import EditIcon from "@material-ui/icons/Edit";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import InfoIcon from "@material-ui/icons/Info";
import GroupWorkIcon from "@material-ui/icons/GroupWork";
import ForumIcon from "@material-ui/icons/Forum";
import FormatQuoteIcon from "@material-ui/icons/FormatQuote";
import CodeIcon from "@material-ui/icons/Code";
import TextFieldsIcon from "@material-ui/icons/TextFields";
import CallSplitIcon from "@material-ui/icons/CallSplit";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import BusinessIcon from "@material-ui/icons/Business";
import AssignmentIcon from "@material-ui/icons/Assignment";
import RecordVoiceOverIcon from "@material-ui/icons/RecordVoiceOver";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import ChatIcon from "@material-ui/icons/Chat";
import api from "../../services/api";
import { getBackendUrl } from "../../config";
import toastError from "../../errors/toastError";
import { TOOL_CATALOG, DEFAULT_SENSITIVE_TOOLS } from "../../constants/aiTools";
import { TOOL_ICONS, TOOL_INSTRUCTIONS } from "./toolConfig";

// Componente Error Boundary simples
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Erro no modal:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Dialog open={this.props.open} onClose={this.props.close}>
          <DialogTitle>Erro</DialogTitle>
          <DialogContent>
            <Typography>Ocorreu um erro ao carregar o formulário. Tente novamente.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.props.close}>Fechar</Button>
          </DialogActions>
        </Dialog>
      );
    }

    return this.props.children;
  }
}

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiDialog-paper": {
      minWidth: "700px",
      maxWidth: "900px"
    }
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: "200px"
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1)
  },
  chip: {
    margin: theme.spacing(0.25)
  },
  providerChip: {
    marginBottom: theme.spacing(2)
  },
  dialogTitle: {
    backgroundColor: "#3f51b5",
    color: "white",
    textAlign: "center",
    fontSize: "24px",
    fontWeight: "bold",
  },
  btnWrapper: {
    position: "relative",
  },
  tabPanel: {
    padding: theme.spacing(2),
    minHeight: "400px"
  },
  tabContent: {
    marginTop: theme.spacing(2)
  },
  promptField: {
    "& .MuiOutlinedInput-root": {
      minHeight: "200px",
      direction: "ltr !important",
      textAlign: "left !important",
      unicodeBidi: "normal !important"
    },
    "& .MuiOutlinedInput-input": {
      direction: "ltr !important",
      textAlign: "left !important",
      unicodeBidi: "normal !important"
    },
    "& .MuiInputBase-inputMultiline": {
      direction: "ltr !important",
      textAlign: "left !important",
      unicodeBidi: "normal !important"
    },
    "& .MuiInputBase-input": {
      direction: "ltr !important",
      textAlign: "left !important",
      unicodeBidi: "normal !important"
    },
    "& textarea": {
      direction: "ltr !important",
      textAlign: "left !important",
      unicodeBidi: "normal !important"
    }
  },
  toolGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(2),
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
  },
  toolCard: {
    padding: theme.spacing(2),
    borderRadius: 14,
    border: "2px solid #e5e7eb",
    display: "flex",
    flexDirection: "column",
    minHeight: 190,
    backgroundColor: "#fff",
    transition: "border-color 0.18s, box-shadow 0.18s, background-color 0.18s",
    cursor: "default",
    '&:hover': {
      boxShadow: "0 4px 16px rgba(99,102,241,0.10)",
      borderColor: "#c7d2fe",
    }
  },
  toolCardActive: {
    border: "2px solid #6366f1",
    backgroundColor: "#f5f3ff",
    '&:hover': {
      boxShadow: "0 4px 16px rgba(99,102,241,0.18)",
    }
  },
  toolHeader: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: theme.spacing(1),
    gap: theme.spacing(1.2),
  },
  toolIcon: {
    width: 38,
    height: 38,
    borderRadius: 10,
    background: "linear-gradient(135deg, #6366f1 0%, #818cf8 100%)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.1rem",
    flexShrink: 0,
    boxShadow: "0 2px 6px rgba(99,102,241,0.25)",
  },
  toolIconSensitive: {
    background: "linear-gradient(135deg, #dc2626 0%, #f87171 100%)",
    boxShadow: "0 2px 6px rgba(220,38,38,0.25)",
  },
  toolTitle: {
    fontWeight: 700,
    fontSize: "0.82rem",
    color: "#1e1b4b",
    lineHeight: 1.3,
  },
  toolSlug: {
    fontSize: "0.68rem",
    color: "#a5b4fc",
    fontFamily: "monospace",
    marginTop: 1,
  },
  toolDescription: {
    fontSize: "0.78rem",
    color: "#4b5563",
    marginBottom: theme.spacing(0.5),
    lineHeight: 1.5,
    flexGrow: 1,
  },
  toolInstruction: {
    fontSize: "0.72rem",
    color: "#9ca3af",
    fontStyle: "italic",
    lineHeight: 1.4,
    marginBottom: theme.spacing(1),
  },
  toolFooter: {
    marginTop: "auto",
    paddingTop: theme.spacing(1),
    borderTop: "1px solid #f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  }
}));

const TabPanel = ({ children, value, index, className }) => (
  <div
    role="tabpanel"
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    style={{ display: value === index ? 'block' : 'none' }}
  >
    <Box className={className}>
      {children}
    </Box>
  </div>
);

const FlowBuilderAddDirectOpenAIModal = ({ open, onSave, data, close }) => {
  const classes = useStyles();
  const [activeModal, setActiveModal] = useState(false);
  const [labels, setLabels] = useState({
    title: "Configurar Agente de IA Direto",
    btn: "Salvar",
  });
  const [tabValue, setTabValue] = useState(0);
  
  const [config, setConfig] = useState({
    aiMode: "system",
    provider: "gemini",
    apiKey: "",
    model: "",
    prompt: "Você é um assistente de IA útil e amigável.",
    temperature: 0.7,
    maxTokens: 1000,
    maxMessages: 10,
    pauseMinutes: 10,
    voice: "texto",
    voiceKey: "",
    voiceRegion: "",
    ttsModel: "tts-1",
    audioPercentage: 30,
    toolsEnabled: [],
    knowledgeBase: [],
    knowledgeBaseIds: [],
    entenderAudio: false,
    entenderImagem: false,
    entenderPdf: false,
    identificarComprovante: false
  });

  const [knowledgeUploading, setKnowledgeUploading] = useState(false);
  const [availableKnowledgeBases, setAvailableKnowledgeBases] = useState([]);
  const [aiIntegrationSettings, setAiIntegrationSettings] = useState([]);
  const [systemProviders, setSystemProviders] = useState({ openai: false, gemini: false, openrouter: false });
  const imageInputRef = useRef(null);
  const [linkForm, setLinkForm] = useState({ title: "", url: "" });
  const [textForm, setTextForm] = useState({ title: "", content: "" });
  const [selectedVoice, setSelectedVoice] = useState("texto");
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [ttsModel, setTtsModel] = useState("tts-1");
  const [audioPercentage, setAudioPercentage] = useState(30);

  const [showFormulario, setShowFormulario] = useState(false);
  const [formulario, setFormulario] = useState({
    nomeEmpresa: '', responsavel: '', telefone: '', email: '', site: '', instagram: '', endereco: '',
    oqueFaz: '', servicos: '', diferencial: '', regioes: '',
    funcaoIA: '', expectativas: '',
    diasAtendimento: '', horario: '', feriados: '',
    precosServicos: '',
    perguntasFrequentes: '',
    atendimentoHoje: '', infoColeta: '', transferencia: '',
    agendamento: '', comoAgendamento: '', dadosAgendamento: '',
    tom: '', palavrasEvitar: '',
    nuncaFazer: '', procedimento: '',
    setoresTransferencia: '',
    baseConhecimento: ''
  });

  const backendBaseUrl = useMemo(() => {
    const url = getBackendUrl();
    if (!url) return "";
    return url.replace(/\/+$/, "");
  }, []);

  const buildPublicUrl = useCallback(
    (path = "") => {
      if (!path) return "";
      if (path.startsWith("http://") || path.startsWith("https://")) {
        return path;
      }
      const sanitized = path.replace(/^\/+/, "");
      if (!backendBaseUrl) {
        return sanitized.startsWith("public/")
          ? `/${sanitized}`
          : `/public/${sanitized}`;
      }
      if (sanitized.startsWith("public/")) {
        return `${backendBaseUrl}/${sanitized}`;
      }
      return `${backendBaseUrl}/public/${sanitized}`;
    },
    [backendBaseUrl]
  );

  const resolveKnowledgeUrl = useCallback(
    (item) => {
      if (!item) return "";
      if (item.url) return buildPublicUrl(item.url);
      if (item.path) return buildPublicUrl(item.path);
      return "";
    },
    [buildPublicUrl]
  );

  const inferKnowledgeType = useCallback((item) => {
    if (item?.type) return item.type;
    if (item?.content) return "text";
    const reference = `${item?.title || ""} ${item?.url || ""} ${item?.path || ""}`.toLowerCase();
    if (/\.(png|jpe?g|gif|bmp|webp|svg)$/.test(reference)) return "image";
    if (/\.pdf$/.test(reference)) return "pdf";
    return "link";
  }, []);

  const handleCopyToClipboard = useCallback(async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Link copiado!");
    } catch (err) {
      console.error(err);
      toastError("Não foi possível copiar o link.");
    }
  }, []);

  const uploadKnowledgeFile = useCallback(async (file) => {
    if (!file) return "";
    setKnowledgeUploading(true);
    const formData = new FormData();
    formData.append("medias", file);

    try {
      const { data } = await api.post("/flowbuilder/content", formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      if (Array.isArray(data)) {
        return data[0];
      }

      if (typeof data === "string") {
        return data;
      }

      return data?.url || data?.path || "";
    } catch (err) {
      console.error(err);
      toastError(err);
      return "";
    } finally {
      setKnowledgeUploading(false);
    }
  }, []);

  const handleKnowledgeFileAdd = useCallback(async (file, type, values, setFieldValue) => {
    if (!file) return;
    const storedPath = await uploadKnowledgeFile(file);

    if (!storedPath) {
      toast.error("Não foi possível enviar o arquivo.");
      return;
    }

    const resource = {
      id: `${type}-${Date.now()}-${file.name}`,
      type,
      title: file.name,
      path: storedPath,
      size: file.size,
      mimeType: file.type,
      createdAt: new Date().toISOString()
    };

    const nextKnowledge = [...(values.knowledgeBase || []), resource];
    setFieldValue("knowledgeBase", nextKnowledge);
    toast.success("Arquivo adicionado à base de conhecimento!");
  }, [uploadKnowledgeFile]);

  const handleRemoveKnowledge = useCallback((resourceIndex, values, setFieldValue) => {
    const current = values.knowledgeBase || [];
    const next = current.filter((_, index) => index !== resourceIndex);
    setFieldValue("knowledgeBase", next);
  }, []);

  const isValidUrl = useCallback((url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }, []);

  const handleAddLinkResource = useCallback((values, setFieldValue) => {
    if (!linkForm.title.trim() || !linkForm.url.trim()) {
      toast.error("Informe o título e o link.");
      return;
    }

    if (!isValidUrl(linkForm.url.trim())) {
      toast.error("Informe um link válido (https://...).");
      return;
    }

    const resource = {
      id: `link-${Date.now()}`,
      type: "link",
      title: linkForm.title.trim(),
      url: linkForm.url.trim(),
      createdAt: new Date().toISOString()
    };

    const next = [...(values.knowledgeBase || []), resource];
    setFieldValue("knowledgeBase", next);
    setLinkForm({ title: "", url: "" });
    toast.success("Link adicionado à base de conhecimento!");
  }, [isValidUrl, linkForm]);

  const handleLinkInputChange = useCallback(
    field => event => {
      const value = event?.target?.value ?? "";
      setLinkForm(prev => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleTextInputChange = useCallback(
    field => event => {
      const value = event?.target?.value ?? "";
      setTextForm(prev => ({ ...prev, [field]: value }));
    },
    []
  );

  // O React executa a funcao de atualizacao DEPOIS, na re-renderizacao, e ate
  // la ele ja reciclou o objeto do evento e zerou o e.target. Ler o valor
  // dentro da funcao dava "Cannot read properties of null (reading 'value')"
  // e derrubava a tela. O valor tem que ser lido agora, fora dela.
  const handleFormulario = (field) => (e) => {
    const valor = e.target.value;
    setFormulario(prev => ({ ...prev, [field]: valor }));
  };

  const gerarPromptFromFormulario = useCallback((setFieldValue) => {
    const f = formulario;
    const linha = (label, valor) => valor?.trim() ? `${label}: ${valor.trim()}` : '';
    const secao = (titulo, linhas) => {
      const conteudo = linhas.filter(Boolean).join('\n');
      return conteudo ? `=== ${titulo} ===\n${conteudo}` : '';
    };

    const partes = [
      secao('DADOS DA EMPRESA', [
        linha('Nome da empresa', f.nomeEmpresa),
        linha('Responsável', f.responsavel),
        linha('Telefone principal', f.telefone),
        linha('E-mail', f.email),
        linha('Site', f.site),
        linha('Instagram', f.instagram),
        linha('Endereço', f.endereco),
      ]),
      secao('SOBRE O NEGÓCIO', [
        linha('O que a empresa faz', f.oqueFaz),
        linha('Principais serviços e produtos', f.servicos),
        linha('Diferencial em relação à concorrência', f.diferencial),
        linha('Cidades e regiões atendidas', f.regioes),
      ]),
      secao('OBJETIVO DA IA', [
        linha('Função principal da IA', f.funcaoIA),
        linha('O que a IA deve resolver', f.expectativas),
      ]),
      secao('HORÁRIO DE ATENDIMENTO', [
        linha('Dias de atendimento', f.diasAtendimento),
        linha('Horário de atendimento', f.horario),
        linha('Atende feriados', f.feriados),
      ]),
      secao('SERVIÇOS E PREÇOS', [
        linha('Lista de serviços com descrição e valor', f.precosServicos),
      ]),
      secao('PERGUNTAS FREQUENTES', [
        linha('Perguntas mais comuns dos clientes', f.perguntasFrequentes),
      ]),
      secao('PROCESSO COMERCIAL', [
        linha('Como funciona o atendimento hoje', f.atendimentoHoje),
        linha('Informações a coletar do cliente', f.infoColeta),
        linha('Quando transferir para humano', f.transferencia),
      ]),
      secao('AGENDAMENTOS', [
        linha('Trabalha com agendamento', f.agendamento),
        linha('Como os agendamentos são feitos hoje', f.comoAgendamento),
        linha('Dados necessários para agendar', f.dadosAgendamento),
      ]),
      secao('TOM DE COMUNICAÇÃO', [
        linha('Como a IA deve falar', f.tom),
        linha('Palavras e expressões a evitar', f.palavrasEvitar),
      ]),
      secao('REGRAS IMPORTANTES', [
        linha('O que a IA nunca deve fazer ou informar', f.nuncaFazer),
        linha('Procedimentos obrigatórios no atendimento', f.procedimento),
      ]),
      secao('TRANSFERÊNCIA PARA EQUIPE', [
        linha('Setores que receberão transferências e quando', f.setoresTransferencia),
      ]),
      secao('BASE DE CONHECIMENTO', [
        linha('Quais bases acessar e em quais situações', f.baseConhecimento),
      ]),
    ].filter(Boolean);

    if (partes.length === 0) {
      toast.warn('Preencha ao menos um campo do formulário antes de gerar.');
      return;
    }

    setFieldValue('prompt', partes.join('\n\n'));
    toast.success('Prompt gerado com sucesso!');
  }, [formulario]);

  const handleAddTextResource = useCallback((values, setFieldValue) => {
    if (!textForm.title.trim() || !textForm.content.trim()) {
      toast.error("Informe o título e o conteúdo do texto.");
      return;
    }

    const resource = {
      id: `text-${Date.now()}`,
      type: "text",
      title: textForm.title.trim(),
      content: textForm.content.trim(),
      createdAt: new Date().toISOString()
    };

    const next = [...(values.knowledgeBase || []), resource];
    setFieldValue("knowledgeBase", next);
    setTextForm({ title: "", content: "" });
    toast.success("Texto adicionado à base de conhecimento!");
  }, [textForm]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const availableTools = TOOL_CATALOG.map(tool => tool.value);

  const openaiModels = [
    { value: "gpt-4o",                label: "GPT-4o — Visão + Áudio" },
    { value: "gpt-4o-mini",           label: "GPT-4o Mini — Visão (econômico)" },
    { value: "gpt-4o-audio-preview",  label: "GPT-4o Audio Preview — Áudio" },
    { value: "gpt-4-turbo",           label: "GPT-4 Turbo — Visão" }
  ];
  const geminiModels = [
    { value: "gemini-2.5-pro",       label: "Gemini 2.5 Pro — Visão + Áudio + Vídeo",  free: false },
    { value: "gemini-2.5-flash",     label: "Gemini 2.5 Flash — Visão + Áudio + Vídeo", free: false },
    { value: "gemini-2.0-flash",     label: "Gemini 2.0 Flash — Visão + Áudio + Vídeo", free: true  },
    { value: "gemini-1.5-pro",       label: "Gemini 1.5 Pro — Visão + Áudio + Vídeo",  free: false },
    { value: "gemini-1.5-flash",     label: "Gemini 1.5 Flash — Visão + Áudio",        free: false },
    { value: "gemini-1.5-flash-8b",  label: "Gemini 1.5 Flash-8B — Visão (leve)",     free: false }
  ];
  const grokModels = [
    { value: "grok-2-vision-latest", label: "Grok 2 Vision — Visão" },
    { value: "grok-2",               label: "Grok 2 — Texto" }
  ];

  const openrouterModels = [
    { value: "openai/gpt-4o",                     label: "GPT-4o — Visão + Áudio (OpenAI)" },
    { value: "openai/gpt-4o-mini",                label: "GPT-4o Mini — Econômico (OpenAI)" },
    { value: "anthropic/claude-3-5-sonnet",       label: "Claude 3.5 Sonnet — Melhor custo/qualidade" },
    { value: "anthropic/claude-3-haiku",          label: "Claude 3 Haiku — Rápido e econômico" },
    { value: "google/gemini-2.0-flash-001",       label: "Gemini 2.0 Flash — Google" },
    { value: "google/gemini-flash-1.5",           label: "Gemini 1.5 Flash — Google" },
    { value: "meta-llama/llama-3.3-70b-instruct", label: "LLaMA 3.3 70B — Meta (grátis)" },
    { value: "meta-llama/llama-3.1-8b-instruct",  label: "LLaMA 3.1 8B — Meta (grátis)" },
    { value: "mistralai/mistral-7b-instruct",     label: "Mistral 7B — Mistral (grátis)" },
    { value: "deepseek/deepseek-chat",            label: "DeepSeek Chat — Econômico" },
  ];

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: "Editar Agente de IA Direto",
        btn: "Salvar",
      });
      const current = data?.data || {};
      try {
        // O corte antigo era 5.000 caracteres, e cortava no meio da palavra: o
        // formulario da empresa gera bem mais que isso, e tudo depois do corte
        // nunca chegava no modelo — a IA respondia generico sem ninguem saber
        // por que. Os modelos atuais aceitam ordens de grandeza a mais; o limite
        // aqui existe so para nao gravar algo absurdo no fluxo.
        const LIMITE_PROMPT = 100000;
        const promptValue = current.prompt || "Você é um assistente de IA útil e amigável.";
        const safePrompt = typeof promptValue === 'string' && promptValue.length <= LIMITE_PROMPT
          ? promptValue
          : String(promptValue).substring(0, LIMITE_PROMPT);
        
        // Recarrega o formulario da empresa se ele foi salvo com o no
        
        if (current.formularioEmpresa && typeof current.formularioEmpresa === "object") {
        
          setFormulario(prev => ({ ...prev, ...current.formularioEmpresa }));
        
        }
        
        setConfig({
          aiMode: current.aiMode || "system",
          provider: current.provider || "gemini",
          apiKey: current.apiKey || "",
          model: current.model || "",
          prompt: safePrompt,
          temperature: current.temperature || 0.7,
          maxTokens: current.maxTokens || 1000,
          entenderAudio: !!current.entenderAudio,
          entenderImagem: !!current.entenderImagem,
          entenderPdf: !!current.entenderPdf,
          identificarComprovante: !!current.identificarComprovante,
          maxMessages: current.maxMessages || 10,
          pauseMinutes: current.pauseMinutes || 10,
          voice: current.voice || "texto",
          voiceKey: current.voiceKey || "",
          voiceRegion: current.voiceRegion || "",
          ttsModel: current.ttsModel || "tts-1",
          audioPercentage: current.audioPercentage || 30,
          toolsEnabled: current.toolsEnabled || [],
          knowledgeBase: current.knowledgeBase || [],
          knowledgeBaseIds: current.knowledgeBaseIds || []
        });
        setSelectedVoice(current.voice || "texto");
        setAudioEnabled(current.voice && current.voice !== "texto");
        setTtsModel(current.ttsModel || "tts-1");
        setAudioPercentage(current.audioPercentage || 30);
      } catch (error) {
        console.error("Erro ao carregar configuração:", error);
        // Fallback para configuração padrão
        setConfig({
          provider: "gemini",
          apiKey: "",
          model: "",
          prompt: "Você é um assistente de IA útil e amigável.",
          temperature: 0.7,
          maxTokens: 1000,
          maxMessages: 10,
          voice: "texto",
          voiceKey: "",
          voiceRegion: "",
          ttsModel: "tts-1",
          audioPercentage: 30,
          toolsEnabled: [],
          knowledgeBase: [],
          knowledgeBaseIds: []
        });
      }
      setActiveModal(true);
    } else if (open === "create") {
      setLabels({
        title: "Adicionar Agente de IA Direto",
        btn: "Salvar",
      });
      setConfig({
        provider: "gemini",
        apiKey: "",
        model: "gemini-2.0-flash",
        prompt: "Você é um assistente de IA útil e amigável.",
        temperature: 0.7,
        maxTokens: 1000,
        maxMessages: 10,
        pauseMinutes: 10,
        voice: "texto",
        voiceKey: "",
        voiceRegion: "",
        ttsModel: "tts-1",
        audioPercentage: 30,
        toolsEnabled: [],
        knowledgeBase: [],
        knowledgeBaseIds: []
      });
      setActiveModal(true);
    }

    return () => {
      // Cleanup
    };
  }, [open, data]);

  useEffect(() => {
    api.get("/knowledge-bases").then(({ data }) => setAvailableKnowledgeBases(data)).catch(() => {});
    api.get("/ai-settings").then(({ data }) => setAiIntegrationSettings(Array.isArray(data) ? data : [])).catch(() => {});
    api.get("/ai-credits/system-providers").then(({ data }) => setSystemProviders(data)).catch(() => {});
  }, []);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleToolToggle = (tool) => {
    setConfig(prev => ({
      ...prev,
      toolsEnabled: prev.toolsEnabled.includes(tool)
        ? prev.toolsEnabled.filter(t => t !== tool)
        : [...prev.toolsEnabled, tool]
    }));
  };

  const handleSave = (values) => {
    handleClose();
    onSave({
      ...values,
      voice: selectedVoice,
      ttsModel: ttsModel,
      audioPercentage: audioPercentage,
      // O formulario so gerava o prompt e sumia ao fechar o modal. Quem quisesse
      // ajustar uma linha tinha que redigitar tudo — e, quando o prompt salvo
      // foi truncado, nao havia de onde regerar. Agora ele fica guardado no no.
      formularioEmpresa: formulario
    });
  };

  const availableModels =
    config.provider === "gemini"     ? geminiModels     :
    config.provider === "grok"       ? grokModels       :
    config.provider === "openrouter" ? openrouterModels :
    openaiModels;

  return (
    <ErrorBoundary open={activeModal} close={handleClose}>
      <Dialog open={activeModal} onClose={handleClose} className={classes.root} maxWidth="md" fullWidth>
        <DialogTitle className={classes.dialogTitle}>
          <Box display="flex" alignItems="center" justifyContent="center">
            <MdSmartToy style={{ marginRight: 8 }} />
            {labels.title}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Formik
            initialValues={config}
            enableReinitialize={true}
            onSubmit={(values, actions) => {
              handleSave(values);
              actions.setSubmitting(false);
            }}
          >
            {({ values, setFieldValue, submitForm }) => (
              <Form style={{ width: "100%" }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="Configurações do Agente IA" variant="scrollable" scrollButtons="auto">
                  <Tab label="1. Prompt do Sistema" />
                  <Tab label="2. Configuração IA" />
                  <Tab label="3. Habilidades" />
                  <Tab label="4. Conhecimento" />
                  <Tab label="5. Voz" />
                </Tabs>

          {/* Aba 1: Prompt do Sistema */}
          <TabPanel value={tabValue} index={0} className={classes.tabPanel}>
            <Box className={classes.tabContent}>
              <Typography variant="h6" style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <ChatIcon style={{ marginRight: 8, color: '#6366f1' }} />
                Prompt do Sistema
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Defina como a IA deve se comportar, seu tom, personalidade e limitações. Seja específico para obter melhores resultados.
              </Typography>

              {/* ── Formulário de Implantação ── */}
              <Box style={{ border: '1px solid #e0e0e0', borderRadius: 12, marginBottom: 20, overflow: 'hidden' }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  onClick={() => setShowFormulario(v => !v)}
                  style={{ padding: '10px 16px', background: '#f3f4ff', cursor: 'pointer', userSelect: 'none' }}
                >
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <AssignmentIcon style={{ color: '#6366f1', fontSize: 20 }} />
                    <Typography style={{ fontWeight: 700, fontSize: 14, color: '#3730a3' }}>
                      Formulário de Implantação
                    </Typography>
                    <Typography variant="caption" style={{ color: '#9e9e9e', marginLeft: 4 }}>
                      Preencha e gere um prompt completo automaticamente
                    </Typography>
                  </Box>
                  <IconButton size="small">
                    {showFormulario ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </Box>

                <Collapse in={showFormulario}>
                  <Box style={{ padding: '16px', maxHeight: 520, overflowY: 'auto' }}>

                    {/* 1. Dados da Empresa */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      1. Dados da Empresa
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="Nome da empresa" value={formulario.nomeEmpresa} onChange={handleFormulario('nomeEmpresa')} /></Grid>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="Nome do responsável" value={formulario.responsavel} onChange={handleFormulario('responsavel')} /></Grid>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="Telefone principal" value={formulario.telefone} onChange={handleFormulario('telefone')} /></Grid>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="E-mail" value={formulario.email} onChange={handleFormulario('email')} /></Grid>
                      <Grid item xs={4}><TextField fullWidth size="small" variant="outlined" label="Site" value={formulario.site} onChange={handleFormulario('site')} /></Grid>
                      <Grid item xs={4}><TextField fullWidth size="small" variant="outlined" label="Instagram" value={formulario.instagram} onChange={handleFormulario('instagram')} /></Grid>
                      <Grid item xs={4}><TextField fullWidth size="small" variant="outlined" label="Endereço" value={formulario.endereco} onChange={handleFormulario('endereco')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 2. Sobre o Negócio */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      2. Sobre o Negócio
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={12}><TextField fullWidth size="small" variant="outlined" label="O que sua empresa faz?" multiline rows={2} value={formulario.oqueFaz} onChange={handleFormulario('oqueFaz')} /></Grid>
                      <Grid item xs={12}><TextField fullWidth size="small" variant="outlined" label="Principais serviços/produtos oferecidos" multiline rows={2} value={formulario.servicos} onChange={handleFormulario('servicos')} /></Grid>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="Diferencial em relação à concorrência" value={formulario.diferencial} onChange={handleFormulario('diferencial')} /></Grid>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="Cidades/regiões atendidas" value={formulario.regioes} onChange={handleFormulario('regioes')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 3. Objetivo da IA */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      3. Objetivo da IA
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={12}><TextField fullWidth size="small" variant="outlined" label="Função principal da IA (ex: Atendimento inicial, Qualificação de leads, Vendas, Suporte...)" value={formulario.funcaoIA} onChange={handleFormulario('funcaoIA')} /></Grid>
                      <Grid item xs={12}><TextField fullWidth size="small" variant="outlined" label="O que você espera que a IA resolva?" multiline rows={2} value={formulario.expectativas} onChange={handleFormulario('expectativas')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 4. Horário de Atendimento */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      4. Horário de Atendimento
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={4}><TextField fullWidth size="small" variant="outlined" label="Dias de atendimento" placeholder="Seg a Sex" value={formulario.diasAtendimento} onChange={handleFormulario('diasAtendimento')} /></Grid>
                      <Grid item xs={4}><TextField fullWidth size="small" variant="outlined" label="Horário" placeholder="08h às 18h" value={formulario.horario} onChange={handleFormulario('horario')} /></Grid>
                      <Grid item xs={4}><TextField fullWidth size="small" variant="outlined" label="Atende feriados?" placeholder="Sim / Não" value={formulario.feriados} onChange={handleFormulario('feriados')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 5. Serviços e Preços */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      5. Serviços e Preços
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={12}><TextField fullWidth size="small" variant="outlined" label="Liste os serviços com nome, descrição e valor" multiline rows={4} placeholder={"Serviço: Consultoria\nDescrição: Análise do negócio\nValor: R$ 500\n\nServiço: Treinamento\n..."} value={formulario.precosServicos} onChange={handleFormulario('precosServicos')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 6. Perguntas Frequentes */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      6. Perguntas Frequentes
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={12}><TextField fullWidth size="small" variant="outlined" label="Quais perguntas seus clientes mais fazem?" multiline rows={3} value={formulario.perguntasFrequentes} onChange={handleFormulario('perguntasFrequentes')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 7. Processo Comercial */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      7. Processo Comercial
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={12}><TextField fullWidth size="small" variant="outlined" label="Como funciona o atendimento hoje?" multiline rows={2} value={formulario.atendimentoHoje} onChange={handleFormulario('atendimentoHoje')} /></Grid>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="Quais informações coletar do cliente?" value={formulario.infoColeta} onChange={handleFormulario('infoColeta')} /></Grid>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="Quando a IA deve transferir para humano?" value={formulario.transferencia} onChange={handleFormulario('transferencia')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 8. Agendamentos */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      8. Agendamentos
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={4}><TextField fullWidth size="small" variant="outlined" label="Trabalha com agendamento?" placeholder="Sim / Não" value={formulario.agendamento} onChange={handleFormulario('agendamento')} /></Grid>
                      <Grid item xs={4}><TextField fullWidth size="small" variant="outlined" label="Como são feitos hoje?" placeholder="Google Agenda, sistema próprio..." value={formulario.comoAgendamento} onChange={handleFormulario('comoAgendamento')} /></Grid>
                      <Grid item xs={4}><TextField fullWidth size="small" variant="outlined" label="Dados a coletar para agendar" value={formulario.dadosAgendamento} onChange={handleFormulario('dadosAgendamento')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 9. Tom de Comunicação */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      9. Tom de Comunicação
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="Como a IA deve falar?" placeholder="Formal, Amigável, Técnica..." value={formulario.tom} onChange={handleFormulario('tom')} /></Grid>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="Palavras/expressões que a IA deve evitar" value={formulario.palavrasEvitar} onChange={handleFormulario('palavrasEvitar')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 10. Regras Importantes */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      10. Regras Importantes
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="O que a IA nunca deve fazer ou informar?" multiline rows={2} value={formulario.nuncaFazer} onChange={handleFormulario('nuncaFazer')} /></Grid>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="Procedimentos obrigatórios no atendimento" multiline rows={2} value={formulario.procedimento} onChange={handleFormulario('procedimento')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 11. Transferência para Equipe */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      11. Transferência para Equipe
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={12}><TextField fullWidth size="small" variant="outlined" label="Setores que receberão transferências e quando" multiline rows={3} placeholder={"Comercial → quando o cliente quiser fechar\nFinanceiro → quando quiser pagar ou segunda via"} value={formulario.setoresTransferencia} onChange={handleFormulario('setoresTransferencia')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 12. Base de Conhecimento */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      12. Base de Conhecimento
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 16 }}>
                      <Grid item xs={12}><TextField fullWidth size="small" variant="outlined" label="Quais bases acessar e em quais situações?" multiline rows={2} value={formulario.baseConhecimento} onChange={handleFormulario('baseConhecimento')} /></Grid>
                    </Grid>

                    {/* Botão Gerar Prompt */}
                    <Box display="flex" justifyContent="flex-end" alignItems="center" style={{ gap: 12, paddingTop: 4 }}>
                      <Typography variant="caption" style={{ color: '#9e9e9e' }}>
                        O conteúdo do prompt será substituído pelas respostas acima
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<AssignmentIcon />}
                        onClick={() => gerarPromptFromFormulario(setFieldValue)}
                        style={{ borderRadius: 20, textTransform: 'none', fontWeight: 700 }}
                      >
                        Gerar Prompt
                      </Button>
                    </Box>

                  </Box>
                </Collapse>
              </Box>

              <Field
                as={TextField}
                label="Prompt do Sistema"
                name="prompt"
                multiline
                rows={18}
                fullWidth
                variant="outlined"
                margin="dense"
                className={classes.formControl}
              />

            </Box>
          </TabPanel>

          {/* Aba 2: Configuração IA */}
          <TabPanel value={tabValue} index={1} className={classes.tabPanel}>
            <Box className={classes.tabContent}>
              <Typography variant="h6" style={{ marginBottom: 16 }}>
                Configuração de IA
              </Typography>


              <Typography variant="h6" style={{ marginTop: 0, marginBottom: 16 }}>
                Parâmetros do Modelo
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Ajuste a criatividade e o tamanho das respostas do modelo de IA.
              </Typography>

              <Box display="flex" gap={2}>
                <Field
                  as={TextField}
                  label="Temperature"
                  name="temperature"
                  type="number"
                  inputProps={{ min: 0, max: 2, step: 1 }}
                  className={classes.formControl}
                  helperText="0=preciso, 1=equilibrado, 2=criativo"
                />
                <Field
                  as={TextField}
                  label="Max Tokens"
                  name="maxTokens"
                  type="number"
                  inputProps={{ min: 1, max: 4096 }}
                  className={classes.formControl}
                  helperText="Tamanho máximo da resposta"
                />
              </Box>

              <Typography variant="h6" style={{ display: 'flex', alignItems: 'center', marginTop: 28, marginBottom: 8 }}>
                <ChatIcon style={{ marginRight: 8, color: '#10b981' }} />
                Reconhecimento
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                Recursos extras para a IA entender o que o cliente envia.
              </Typography>

              <Box style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 12, marginBottom: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={!!values.entenderImagem}
                      onChange={(_, v) => setFieldValue("entenderImagem", v)}
                    />
                  }
                  label={<span style={{ fontSize: 14 }}>Entender imagem</span>}
                />
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={!!values.entenderPdf}
                      onChange={(_, v) => setFieldValue("entenderPdf", v)}
                    />
                  }
                  label={<span style={{ fontSize: 14 }}>Entender PDF</span>}
                />
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={!!values.entenderAudio}
                      onChange={(_, v) => setFieldValue("entenderAudio", v)}
                    />
                  }
                  label={<span style={{ fontSize: 14 }}>Entender áudio do cliente</span>}
                />
                <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                  A IA lê a mídia que o cliente enviou. O áudio é transcrito antes.
                  Essas opções consomem mais tokens.
                </Typography>
              </Box>

              <Box style={{ border: "1px solid #fdba74", background: "#fff7ed", borderRadius: 10, padding: 12, marginBottom: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={!!values.identificarComprovante}
                      onChange={(_, v) => setFieldValue("identificarComprovante", v)}
                    />
                  }
                  label={<span style={{ fontSize: 14, fontWeight: 600, color: "#9a3412" }}>Identificar comprovante</span>}
                />
                <Typography variant="caption" style={{ display: "block", color: "#9a3412" }}>
                  A IA analisa a imagem ou PDF enviado e extrai os dados do pagamento
                  (valor, data, pagador, recebedor) e diz se parece autêntico.
                </Typography>
              </Box>

              <Typography variant="h6" style={{ display: 'flex', alignItems: 'center', marginTop: 28, marginBottom: 8 }}>
                <ChatIcon style={{ marginRight: 8, color: '#6366f1' }} />
                Histórico de Mensagens
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Quantidade de mensagens anteriores que a IA deve considerar no contexto da conversa.
              </Typography>
              <Field
                as={TextField}
                label="Máximo de Mensagens"
                name="maxMessages"
                type="number"
                inputProps={{ min: 1, max: 50, step: 1 }}
                fullWidth
                className={classes.formControl}
                helperText="Recomendado: 10 mensagens"
              />

              <Typography variant="h6" style={{ display: 'flex', alignItems: 'center', marginTop: 28, marginBottom: 8 }}>
                <ScheduleIcon style={{ marginRight: 8, color: '#6366f1' }} />
                Pausa da IA ao Responder Manualmente
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Quando você responder manualmente pelo sistema ou celular, a IA ficará pausada por este tempo antes de voltar a responder automaticamente.
              </Typography>
              <Field
                as={TextField}
                label="Tempo de Pausa (minutos)"
                name="pauseMinutes"
                type="number"
                inputProps={{ min: 1, max: 1440, step: 1 }}
                fullWidth
                className={classes.formControl}
                helperText="Padrão: 10 minutos. Máximo: 1440 (24h)"
              />
            </Box>
          </TabPanel>

          {/* Aba 3: Habilidades */}
          <TabPanel value={tabValue} index={2} className={classes.tabPanel}>
            <Box className={classes.tabContent}>
              <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 6 }}>
                <Typography variant="h6" style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 16 }}>
                  <ExtensionIcon style={{ color: '#6366f1', fontSize: 20 }} />
                  Habilidades do Agente
                </Typography>
                <Typography variant="caption" style={{ background: '#f3f4f6', borderRadius: 20, padding: '4px 12px', color: '#6b7280', fontWeight: 600 }}>
                  {(values.toolsEnabled || []).length} ativa{(values.toolsEnabled || []).length !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Ative somente as habilidades que este agente deve usar. As marcadas em vermelho são sensíveis — ative apenas se necessário.
              </Typography>
              <Box className={classes.toolGrid}>
                {TOOL_CATALOG.map(tool => {
                  const checked = (values.toolsEnabled || []).includes(tool.value);
                  const isSensitive = DEFAULT_SENSITIVE_TOOLS.includes(tool.value);
                  const Icon = TOOL_ICONS[tool.value] || MdSmartToy;
                  return (
                    <Paper
                      key={tool.value}
                      className={`${classes.toolCard} ${checked ? classes.toolCardActive : ''}`}
                      elevation={0}
                    >
                      <div className={classes.toolHeader}>
                        <div className={`${classes.toolIcon} ${isSensitive ? classes.toolIconSensitive : ''}`}>
                          <Icon style={{ fontSize: 18 }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Typography className={classes.toolTitle}>
                            {tool.title}
                          </Typography>
                          <Typography className={classes.toolSlug}>
                            {tool.value}
                          </Typography>
                        </div>
                        {isSensitive && (
                          <Chip
                            size="small"
                            label="Sensível"
                            style={{ backgroundColor: "#fee2e2", color: "#b91c1c", fontSize: 10, height: 20, flexShrink: 0 }}
                          />
                        )}
                      </div>
                      <Typography className={classes.toolDescription}>
                        {tool.description}
                      </Typography>
                      <Typography className={classes.toolInstruction}>
                        {TOOL_INSTRUCTIONS[tool.value] || "Instrua a IA mencionando o nome da ferramenta quando precisar usá-la."}
                      </Typography>
                      <div className={classes.toolFooter}>
                        <Typography variant="caption" style={{ color: checked ? "#6366f1" : "#9ca3af", fontWeight: checked ? 600 : 400 }}>
                          {checked ? "● Ativa" : "○ Desativada"}
                        </Typography>
                        <Switch
                          color="primary"
                          checked={checked}
                          onChange={(_, newValue) => {
                            const current = values.toolsEnabled || [];
                            const next = newValue
                              ? [...current, tool.value]
                              : current.filter(name => name !== tool.value);
                            setFieldValue("toolsEnabled", next);
                          }}
                        />
                      </div>
                    </Paper>
                  );
                })}
              </Box>
            </Box>
          </TabPanel>

          {/* Aba 4: Base de Conhecimento */}
          <TabPanel value={tabValue} index={3} className={classes.tabPanel}>
            <Box className={classes.tabContent}>
              <Typography variant="subtitle2" gutterBottom>
                <LibraryBooksIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                Bases de Conhecimento
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Selecione as bases de conhecimento que este agente pode consultar. Nas bases vinculadas, instrua a IA no prompt sobre quando e como usá-las.
              </Typography>

              {availableKnowledgeBases.length === 0 ? (
                <Paper style={{ padding: 24, textAlign: 'center', border: '1px dashed #d1d5db', backgroundColor: '#f9fafb' }}>
                  <LibraryBooksIcon style={{ fontSize: 40, color: '#ccc', marginBottom: 8 }} />
                  <Typography variant="body2" color="textSecondary">
                    Nenhuma base de conhecimento criada ainda.
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Acesse <strong>Automação → Bases de Conhecimento</strong> para criar suas bases.
                  </Typography>
                </Paper>
              ) : (
                <Grid container spacing={1}>
                  {availableKnowledgeBases.map(base => {
                    const isSelected = (config.knowledgeBaseIds || []).includes(base.id);
                    return (
                      <Grid item xs={12} sm={6} key={base.id}>
                        <Paper
                          style={{
                            padding: 12,
                            border: `2px solid ${isSelected ? '#6366f1' : '#e5e7eb'}`,
                            backgroundColor: isSelected ? '#eef2ff' : '#fff',
                            cursor: 'pointer',
                            borderRadius: 8
                          }}
                          onClick={() => {
                            const ids = config.knowledgeBaseIds || [];
                            setConfig(prev => ({
                              ...prev,
                              knowledgeBaseIds: isSelected
                                ? ids.filter(id => id !== base.id)
                                : [...ids, base.id]
                            }));
                          }}
                        >
                          <Box display="flex" alignItems="flex-start" style={{ gap: 10 }}>
                            <LibraryBooksIcon color={isSelected ? "primary" : "action"} fontSize="small" style={{ marginTop: 2 }} />
                            <Box flex={1}>
                              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>{base.name}</Typography>
                              {base.description && (
                                <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                                  {base.description}
                                </Typography>
                              )}
                              <Chip
                                label={`${base.items?.length || 0} itens`}
                                size="small"
                                style={{ marginTop: 4, fontSize: 10, height: 20 }}
                                color={isSelected ? "primary" : "default"}
                              />
                            </Box>
                            {isSelected && (
                              <Chip label="Vinculada" color="primary" size="small" style={{ fontSize: 10, height: 20 }} />
                            )}
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              )}

              {(config.knowledgeBaseIds || []).length > 0 && (
                <Box style={{ marginTop: 16, padding: 12, backgroundColor: '#f0f4ff', borderRadius: 8, border: '1px solid #c5d0f5' }}>
                  <Typography variant="caption" color="textSecondary">
                    <strong>Dica:</strong> No prompt deste agente, instrua a IA sobre quando consultar cada base. Exemplo: <em>"Quando o contato perguntar sobre tratamentos, consulte a base de conhecimento 'Tratamentos' para obter as informações corretas."</em>
                  </Typography>
                </Box>
              )}
            </Box>
          </TabPanel>

          {/* Aba 5: Voz */}
          <TabPanel value={tabValue} index={4} className={classes.tabPanel}>
            <Box className={classes.tabContent}>
              <Typography variant="h6" style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <RecordVoiceOverIcon style={{ marginRight: 8, color: '#6366f1' }} />
                Configuração de Resposta por Áudio
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Configure como a IA responderá: texto ou áudio usando OpenAI TTS.
              </Typography>

              <Paper style={{ padding: 16, marginBottom: 16, backgroundColor: "#f8f9fa" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={audioEnabled}
                      onChange={(e) => {
                        setAudioEnabled(e.target.checked);
                        setSelectedVoice(e.target.checked ? "alloy" : "texto");
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center">
                      <VolumeUpIcon style={{ marginRight: 8 }} />
                      <Typography variant="body1">
                        Habilitar resposta por áudio
                      </Typography>
                    </Box>
                  }
                />
              </Paper>

              {audioEnabled && (
                <>
                  <Paper style={{ padding: 16, marginBottom: 16, backgroundColor: "#e3f2fd", border: "1px solid #90caf9" }}>
                    <Box display="flex" alignItems="center">
                      <InfoIcon style={{ marginRight: 8, color: "#1976d2" }} />
                      <Typography variant="body2" style={{ color: "#1565c0" }}>
                        Usa a mesma API Key configurada na aba "Configuração IA"
                      </Typography>
                    </Box>
                  </Paper>

                  <FormControl variant="outlined" className={classes.formControl} margin="dense" fullWidth>
                    <InputLabel>Modelo TTS</InputLabel>
                    <Select
                      value={ttsModel}
                      onChange={(e) => setTtsModel(e.target.value)}
                      label="Modelo TTS"
                    >
                      <MenuItem value="tts-1">
                        <Box>
                          <Typography variant="body1">tts-1</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Rápido e eficiente
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="tts-1-hd">
                        <Box>
                          <Typography variant="body1">tts-1-hd</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Alta qualidade (mais lento)
                          </Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <Paper style={{ padding: 16, marginBottom: 16, backgroundColor: "#fff3e0", border: "1px solid #ffb74d" }}>
                    <Typography variant="subtitle2" style={{ marginBottom: 12, fontWeight: 600, color: "#e65100" }}>
                      🎲 Probabilidade de Resposta em Áudio
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                      Controle a porcentagem de respostas que serão enviadas em áudio. O restante será enviado como texto.
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography variant="body2" style={{ minWidth: 40, fontWeight: 600, color: "#e65100" }}>
                        {audioPercentage}%
                      </Typography>
                      <Slider
                        value={audioPercentage}
                        onChange={(e, newValue) => setAudioPercentage(newValue)}
                        min={0}
                        max={100}
                        step={5}
                        marks={[
                          { value: 0, label: '0%' },
                          { value: 25, label: '25%' },
                          { value: 50, label: '50%' },
                          { value: 75, label: '75%' },
                          { value: 100, label: '100%' }
                        ]}
                        valueLabelDisplay="auto"
                        style={{ flex: 1 }}
                      />
                    </Box>
                  </Paper>

                  <FormControl variant="outlined" className={classes.formControl} margin="dense" fullWidth>
                    <InputLabel>Voz</InputLabel>
                    <Select
                      value={selectedVoice}
                      onChange={(e) => setSelectedVoice(e.target.value)}
                      label="Voz"
                    >
                      <MenuItem value="alloy">
                        <Box>
                          <Typography variant="body1">Alloy</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Neutro e equilibrado
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="echo">
                        <Box>
                          <Typography variant="body1">Echo</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Masculino e claro
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="fable">
                        <Box>
                          <Typography variant="body1">Fable</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Britânico e expressivo
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="onyx">
                        <Box>
                          <Typography variant="body1">Onyx</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Grave e profundo
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="nova">
                        <Box>
                          <Typography variant="body1">Nova</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Feminino e energético
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="shimmer">
                        <Box>
                          <Typography variant="body1">Shimmer</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Feminino e suave
                          </Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <Paper style={{ padding: 16, marginTop: 16, backgroundColor: "#f5f5f5" }}>
                    <Typography variant="subtitle2" style={{ marginBottom: 12, fontWeight: 600 }}>
                      🎧 Preview das Vozes:
                    </Typography>
                    <Grid container spacing={2}>
                      {[
                        { name: "alloy", label: "Alloy", desc: "Neutro e equilibrado", url: "https://cdn.openai.com/API/docs/audio/alloy.wav" },
                        { name: "echo", label: "Echo", desc: "Masculino e claro", url: "https://cdn.openai.com/API/docs/audio/echo.wav" },
                        { name: "fable", label: "Fable", desc: "Britânico e expressivo", url: "https://cdn.openai.com/API/docs/audio/fable.wav" },
                        { name: "onyx", label: "Onyx", desc: "Grave e profundo", url: "https://cdn.openai.com/API/docs/audio/onyx.wav" },
                        { name: "nova", label: "Nova", desc: "Feminino e energético", url: "https://cdn.openai.com/API/docs/audio/nova.wav" },
                        { name: "shimmer", label: "Shimmer", desc: "Feminino e suave", url: "https://cdn.openai.com/API/docs/audio/shimmer.wav" }
                      ].map((voice) => (
                        <Grid item xs={12} sm={6} key={voice.name}>
                          <Paper 
                            style={{ 
                              padding: 12, 
                              backgroundColor: selectedVoice === voice.name ? "#e3f2fd" : "#fff",
                              border: selectedVoice === voice.name ? "2px solid #1976d2" : "1px solid #ddd",
                              cursor: "pointer",
                              transition: "all 0.2s"
                            }}
                            onClick={() => setSelectedVoice(voice.name)}
                          >
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                              <Box flex={1}>
                                <Typography variant="body1" style={{ fontWeight: 600 }}>
                                  {voice.label}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {voice.desc}
                                </Typography>
                              </Box>
                              <Tooltip title="Ouvir preview">
                                <IconButton 
                                  size="small" 
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const audio = new Audio(voice.url);
                                    audio.play().catch(err => console.log("Erro ao reproduzir áudio:", err));
                                  }}
                                >
                                  <PlayArrowIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </>
              )}
            </Box>
          </TabPanel>
              </Form>
            )}
          </Formik>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            startIcon={<CancelIcon />}
            style={{
              color: "white",
              backgroundColor: "#db6565",
            }}
            variant="contained"
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              const form = document.querySelector('form');
              if (form) {
                form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
              }
            }}
            startIcon={<SaveIcon />}
            style={{
              color: "white",
              backgroundColor: "#437db5",
            }}
            variant="contained"
            className={classes.btnWrapper}
          >
            {labels.btn}
          </Button>
        </DialogActions>
      </Dialog>
    </ErrorBoundary>
  );
};

export default FlowBuilderAddDirectOpenAIModal;
